import { useEffect } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { usePreviousPage, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { ProgressBar, User } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { AchievementSupervisedUsers } from '.';

import type { GetAchievementSupervisorsRes } from 'shared-types/responses';

interface Props {
  handleNotFound: () => void;
  supervisorId: number;
}

const AchievementSupervisorsSupervisor = ({
  handleNotFound,
  supervisorId
}: Props) => {
  const {
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const { as, href, title } = usePreviousPage({});

  const { data, loading } = useApiData<GetAchievementSupervisorsRes>({
    url: `/admin/achievements/${achievement.id}/supervisors?id=${supervisorId}`,
    onlyIf: Boolean(achievement.id && supervisorId)
  });

  useEffect(() => {
    if (!loading && !data?.supervisors?.length) handleNotFound();
  }, [data, handleNotFound, loading]);

  const showLinkToMyTeamPage = /\/my-team\//.test(href);

  const supervisor = data?.supervisors[0];

  if (!loading && !supervisor) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      <Box
        headerContent={
          showLinkToMyTeamPage ? (
            <BoxBackLink as={as} href={href} text={title} />
          ) : (
            <BoxBackLink
              as={`/admin/achievements/${achievement.id}/supervisors`}
              href="/admin/achievements/[achievementId]/supervisors"
              text={messages.commonAllSupervisors}
            />
          )
        }
        loading={loading}
      >
        {supervisor && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <User enableProfileLink={true} user={supervisor} />

            <ProgressBar
              label={getMessageWithPlaceholders({
                message: messages.usersNumberOfUsers,
                placeholders: {
                  numberOfUsers: `${supervisor.numberOfCompletions} / ${supervisor.numberOfSupervisedUsers}`
                }
              })}
              color={theme.successColor}
              progress={
                supervisor.numberOfCompletions /
                supervisor.numberOfSupervisedUsers
              }
              style={{ minWidth: 100, width: 'fit-content' }}
            />
          </div>
        )}
      </Box>

      {supervisor && (
        <AchievementSupervisedUsers
          achievementId={achievement.id}
          supervisorId={supervisorId}
        />
      )}
    </div>
  );
};

export default AchievementSupervisorsSupervisor;
